import { pxToRem, theme as CoreTheme } from '@back-office/core';
import { useInject } from '@back-office/submerchant';
import { GcpBox, GcpTypography, ToyotaLogo } from '@back-office/ui';
import { IconButton, useMediaQuery } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(
  (theme) => ({
    appbar: {
      background: theme.palette.common.white,
      boxShadow: '0 4px 24px rgba(26, 52, 75, 0.06)',
    },
    menuButton: {
      position: 'relative',
      marginRight: theme.spacing(1),
    },
    title: {
      flexGrow: 1,
      position: 'relative',
    },
    divider: {
      height: '200%',
      bottom: theme.spacing(-1.5),
      minHeight: '45px',
    },
    merchant: {
      fontSize: pxToRem(10),
      marginLeft: theme.spacing(1),
    },
    logo: {
      width: theme.spacing(6),
    },
    height: {
      height: '100%',
    },
    merchantName: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    button: {
      display: 'ruby',
    },
    signOut: {
      fontSize: pxToRem(12),
      fontWeight: 400,
    },
    [theme.breakpoints.up('lg')]: {
      logo: {
        width: theme.spacing(10),
      },
      merchantName: {
        marginLeft: theme.spacing(2),
      },
      menuButton: {
        marginRight: theme.spacing(2),
      },
    },
  }),
  { name: 'Appbar' }
);

const GcpAppBar: FC = observer(() => {
  const isDesktop = useMediaQuery(CoreTheme.breakpoints.up('lg'));
  const classes = useStyles();
  const { t } = useTranslation();
  const { logout, auth } = useInject((root) => root);
  const user = useMemo(
    () => auth.currentUser?.email?.split('@')[0] || 'Unknown',
    [auth.currentUser?.email]
  );

  const handleLogout = (): void => {
    logout();
  };

  const role = useMemo(
    () =>
      t('appbar.role', { role: auth.currentUser.role })
        .toLocaleLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase()),
    [auth.currentUser.role, t]
  );

  return (
    <AppBar className={classes.appbar} color="default" position="static">
      <Toolbar className={classes.height}>
        <div className={classes.menuButton}>
          <IconButton aria-label="menu" color="inherit" edge="start">
            <ToyotaLogo className={classes.logo} />
          </IconButton>
        </div>
        <div className={classes.title}>
          {isDesktop && (
            <Divider
              absolute
              className={classes.divider}
              orientation="vertical"
            />
          )}
          <div className={classes.merchantName}>
            <GcpTypography
              paint="hitGray"
              variant={isDesktop ? 'h3' : 'caption'}
            >
              {t('appbar.sub_merchant', {
                mid: auth.currentUser.mid,
                name: decodeURIComponent(auth.currentUser.merchantName),
              })}
            </GcpTypography>
          </div>
        </div>
        <GcpBox display="ruby" mr={isDesktop ? 2 : 1}>
          <GcpTypography
            display="inline"
            paint="hitGray"
            variant={isDesktop ? 'body1' : 'caption'}
          >
            {role}
          </GcpTypography>
          <GcpTypography
            display="inline"
            paint="black"
            variant={isDesktop ? 'body1' : 'caption'}
          >
            {user}
          </GcpTypography>
        </GcpBox>
        {isDesktop && (
          <GcpBox mr={isDesktop ? 2 : 1}>
            <Divider className={classes.divider} orientation="vertical" />
          </GcpBox>
        )}
        <Button
          className={clsx(classes.button, { [classes.signOut]: !isDesktop })}
          color="inherit"
          onClick={handleLogout}
        >
          {t('appbar.sign_out')}
        </Button>
      </Toolbar>
    </AppBar>
  );
});

export default GcpAppBar;
